import React, { useState, useEffect } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  DatePicker,
  Tag,
  message,
  Divider,
} from "antd";
import dayjs from "dayjs";
import { SearchOutlined, FileExcelOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getLemburList } from "../../api/transaksi/Lembur";

const { RangePicker } = DatePicker;

export default function Lembur() {
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [displayedRows, setDisplayedRows] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);

  let isAdmin = false;
  const sessiData = JSON.parse(sessionStorage.getItem("user"));
  if (
    sessiData &&
    Array.isArray(sessiData) &&
    sessiData[0]?.user?.[0]?.rolename
  ) {
    const adminRoles = JSON.parse(process.env.REACT_APP_ROLEADMIN || '[]');
    isAdmin = adminRoles.includes(sessiData[0].user[0].rolename);
  }

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const handleSearch = (value) => {
    setSearchText(value);
    const filtered = data.filter((item) =>
      item.karyawan?.emp_nama?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredData(filtered);
  };

  const handleExportExcel = () => {
    const exportSource =
      displayedRows.length > 0
        ? displayedRows
        : searchText
        ? filteredData
        : data;

    if (!exportSource || exportSource.length === 0) {
      message.warning("Tidak ada data untuk diekspor.");
      return;
    }

    const exportData = exportSource.map((item) => {
      const row = {};
      if (isAdmin) {
        row["Nama Karyawan"] = item.nama_karyawan || "-";
      }

      row["Tanggal Lembur"] = item.ovt_tanggal_lembur
        ? dayjs(item.ovt_tanggal_lembur).format("YYYY-MM-DD")
        : "-";

      row["Biaya Lembur"] = item.ovt_biaya_lembur || 0;
      row["Keterangan"] = item.ovt_keterangan || "-";
      row["Status Pengajuan"] = item.ovt_status || "-";
      row["Status Pembayaran"] = item.ovt_status_pembayaran || "-";
      row["Status Pembayaran"] = item.ovt_status_pembayaran || "-";
      row["Tanggal dibayar"] = item.ovt_tanggal_dibayar
        ? dayjs(item.ovt_tanggal_dibayar).format("YYYY-MM-DD")
        : "-";

      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Laporan Lembur");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `Laporan_Lembur_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  const columns = [
    ...(isAdmin
      ? [
          {
            title: "Nama Karyawan",
            dataIndex: "nama_karyawan",
            key: "nama_karyawan",
            filterDropdown: ({
              setSelectedKeys,
              selectedKeys,
              confirm,
              clearFilters,
            }) => (
              <div style={{ padding: 8 }}>
                <Input
                  placeholder="Cari nama"
                  value={selectedKeys[0]}
                  onChange={(e) =>
                    setSelectedKeys(e.target.value ? [e.target.value] : [])
                  }
                  onPressEnter={confirm}
                  style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={confirm}
                    size="small"
                  >
                    Cari
                  </Button>
                  <Button
                    onClick={() => {
                      clearFilters();
                      confirm();
                    }}
                    size="small"
                  >
                    Reset
                  </Button>
                </Space>
              </div>
            ),
            onFilter: (value, record) =>
              record.nama_karyawan?.toLowerCase().includes(value.toLowerCase()),
          },
        ]
      : []),
    {
      title: "Tanggal Lembur",
      dataIndex: "ovt_tanggal_lembur",
      sorter: (a, b) =>
        dayjs(a.ovt_tanggal_lembur).unix() - dayjs(b.ovt_tanggal_lembur).unix(),
      sortDirections: ["descend", "ascend"],
      render: (value) => {
        const date = dayjs(value);
        return date.isValid() ? date.format("DD/MM/YYYY") : "-";
      },
    },
    {
      title: "Biaya Lembur",
      dataIndex: "ovt_biaya_lembur",
      render: (val) => (val ? `Rp ${val.toLocaleString()}` : ""),
      sorter: (a, b) => a.ovt_biaya_lembur - b.ovt_biaya_lembur,
    },
    {
      title: "Keterangan",
      dataIndex: "ovt_keterangan",
    },
    {
      title: "Status Pengajuan",
      dataIndex: "ovt_status",
      filters: [
        { text: "Pending", value: "pending" },
        { text: "Disetujui", value: "disetujui" },
        { text: "Ditolak", value: "ditolak" },
      ],
      onFilter: (value, record) => record.ovt_status === value,
      render: (status) => {
        if (!status || status === "pending") return "-";

        const colorMap = {
          pending: "orange",
          disetujui: "green",
          ditolak: "red",
        };
        return <Tag color={colorMap[status]}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: "Status Pembayaran",
      dataIndex: "ovt_status_pembayaran",
      filters: [
        { text: "Tidak Dibayar", value: "tidak dibayar" },
        { text: "Lunas", value: "lunas" },
      ],
      onFilter: (value, record) => record.ovt_status_pembayaran === value,
      render: (status) => {
        if (!status || status === "pending") return "-";

        const colorMap = {
          "belum dibayar": "volcano",
          lunas: "blue",
          "tidak dibayar": "gray",
        };
        const labelMap = {
          "belum dibayar": "Belum Dibayar",
          lunas: "Lunas",
          "tidak dibayar": "Tidak Dibayar",
        };
        return (
          <Tag color={colorMap[status]}>{labelMap[status]?.toUpperCase()}</Tag>
        );
      },
    },
    {
      title: "Tanggal Pembayaran",
      dataIndex: "ovt_tanggal_dibayar",
      sorter: (a, b) => {
        const dateA = dayjs(a.ovt_tanggal_dibayar);
        const dateB = dayjs(b.ovt_tanggal_dibayar);

        const isValidA = dateA.isValid();
        const isValidB = dateB.isValid();

        if (!isValidA && !isValidB) return 0;
        if (!isValidA) return 1;
        if (!isValidB) return -1;

        return dateA.unix() - dateB.unix();
      },
      sortDirections: ["descend", "ascend"],
      render: (value) => {
        const date = dayjs(value);
        return date.isValid() ? date.format("DD/MM/YYYY") : "-";
      },
    },
  ];

  const fetchData = async () => {
    setLoadingTable(true);
    try {
      const [startDate, endDate] = dateRange || [];

      const formattedStartDate = startDate
        ? startDate.format("YYYY-MM-DD")
        : null;
      const formattedEndDate = endDate ? endDate.format("YYYY-MM-DD") : null;

      const result = await getLemburList(
        isAdmin ? "" : sessiData[0].user[0].employeeId,
        formattedStartDate,
        formattedEndDate
      );

      if (result.status) {
        setData(result.data);
      } else {
        setData([]);
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Terjadi kesalahan saat mengambil data";
      message.error(errorMessage);
      setData([]);
    } finally {
      setLoadingTable(false);
    }
  };

  const filteredByDate = () => {
    if (!dateRange || dateRange.length === 0) return data;

    return data.filter((item) => {
      const lemburDate = dayjs(item.ovt_tanggal_lembur);
      return (
        lemburDate.isAfter(dateRange[0].startOf("day")) &&
        lemburDate.isBefore(dateRange[1].endOf("day"))
      );
    });
  };

  const finalData = searchText ? filteredData : filteredByDate();

  return (
    <div>
      <Divider>Laporan Lembur Karyawan</Divider>
      <Space style={{ marginBottom: 16 }}>
        <label>Periode:</label>
        <RangePicker
          value={dateRange}
          onChange={(range) => setDateRange(range)}
        />

        <Button
          icon={<FileExcelOutlined />}
          type="primary"
          onClick={handleExportExcel}
        >
          Export Excel
        </Button>
      </Space>
      <Table
        columns={columns}
        dataSource={finalData}
        loading={loadingTable}
        rowKey="ovt_id_lembur"
        pagination={{
          pageSizeOptions: ["10", "20", "40", "60"],
          showSizeChanger: true,
          defaultPageSize: 20,
        }}
        onChange={(_, __, ___, extra) => {
          setDisplayedRows(extra.currentDataSource);
        }}
      />
    </div>
  );
}
